import { useEffect, useRef, useState } from 'react';
import { getInstagramCommentReplyOperation, replyInstagramComment,
  type InstagramCommentData, type InstagramContactIdentity, type InstagramSendResult } from '../services/instagram.ts';

type Pending = { key: string; content: string; uncertain: boolean };

/** Public reply drafts are separate from Direct and stay bound to the comment. */
export function useInstagramComments(contactId: number | null, identity: InstagramContactIdentity | null,
  onSent: (result: InstagramSendResult) => void) {
  const [target, setTarget] = useState<InstagramCommentData | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const [draftLocked, setDraftLocked] = useState(false);
  const selected = useRef(contactId); selected.current = contactId;
  const busy = useRef(false);
  const pending = useRef(new Map<string, Pending>());
  const fingerprint = (id: number) => JSON.stringify([localStorage.getItem('clinic_id'), contactId, identity?.identity_id, id]);
  useEffect(() => { setTarget(null); setDraft(''); setError(''); setUncertain(false); setDraftLocked(false); }, [contactId]);
  const canReply = !!contactId && identity?.contact_id === contactId && !!identity.capabilities.reply_comment;

  const select = (comment: InstagramCommentData) => {
    if (!canReply || busy.current || comment.is_public_reply || comment.status !== 'available') return;
    const old = pending.current.get(fingerprint(comment.id));
    setTarget(comment); setDraft(old?.content || ''); setUncertain(!!old?.uncertain); setDraftLocked(!!old); setError('');
  };
  const cancel = () => { if (!busy.current) { setTarget(null); setDraft(''); setError(''); setUncertain(false); setDraftLocked(false); } };

  const execute = async (verify: boolean) => {
    if (!contactId || !identity || identity.contact_id !== contactId || !target || busy.current || (!verify && (!canReply || !draft.trim() || uncertain))) return;
    const id = contactId, key = fingerprint(target.id);
    let operation = pending.current.get(key);
    if (!operation) {
      operation = { key: `ig-comment:${id}:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`, content: draft, uncertain: true };
      pending.current.set(key, operation);
    }
    busy.current = true; setSending(true); setDraftLocked(true); setError('');
    try {
      const result = verify
        ? await getInstagramCommentReplyOperation({ contactId: id, identityId: identity.identity_id }, operation.key)
        : await replyInstagramComment(target.id, { contact_id: id, identity_id: identity.identity_id, idempotency_key: operation.key, content: operation.content });
      operation.uncertain = result.status !== 'sent' && result.status !== 'failed';
      if (!operation.uncertain) pending.current.delete(key);
      if (selected.current !== id) return;
      setUncertain(operation.uncertain);
      setDraftLocked(operation.uncertain);
      if (result.status === 'sent') { onSent(result); setTarget(null); setDraft(''); }
      else setError(result.status === 'failed' ? 'Não foi possível publicar a resposta. Verifique a conexão e se o comentário ainda existe.'
        : 'Resposta não confirmada. O texto foi preservado. Verifique o resultado antes de publicar novamente.');
    } catch (cause: any) {
      if (verify && cause?.response?.status === 404) {
        // An explicit new click may submit the ORIGINAL text with the SAME key.
        // If the previous request later commits, backend idempotency still wins.
        operation.uncertain = false;
        if (selected.current === id) { setUncertain(false); setDraftLocked(true); setError('Operação ainda não encontrada. Você pode publicar o texto preservado com a mesma identificação.'); }
        return;
      }
      const knownRejected = cause?.response?.status >= 400 && cause?.response?.status < 500;
      if (knownRejected && !verify) pending.current.delete(key);
      if (selected.current === id) {
        setUncertain(!knownRejected || verify);
        setDraftLocked(!knownRejected || verify);
        setError(knownRejected && !verify ? 'A resposta foi bloqueada. Verifique o acesso, a conexão e o comentário.' : 'Resposta não confirmada. Verifique o resultado; nenhuma nova publicação foi feita automaticamente.');
      }
    } finally { busy.current = false; setSending(false); }
  };
  return { target, draft, setDraft, draftLocked, sending, error, uncertain, canReply, select, cancel,
    publish: () => execute(false), verify: () => execute(true) };
}
